<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { ClipboardCopy, Download, RefreshCw } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import {
    store as citePaper,
    updateStyle,
} from '@/actions/App/Http/Controllers/CitationController';
import { store as referencesStore } from '@/actions/App/Http/Controllers/ReferencesController';
import BibliographyView from '@/components/BibliographyView.vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import ReferencesPanel from '@/components/references/ReferencesPanel.vue';
import { Button } from '@/components/ui/button';
import { useToast } from '@/composables/useToast';
import { downloadBibTeX } from '@/lib/citations/bibtex';
import { copyBibliography, sortForStyle } from '@/lib/citations/formatters';
import type { CitationStyle } from '@/lib/citations/formatters';
import type { Project } from '@/types';

const props = defineProps<{
    project: Project;
    references: ReferenceItem[];
    zotero: {
        connected: boolean;
        last_synced_at: string | null;
        collections: string[];
    };
    citedIds: ReadonlySet<string>;
    citedOrder: string[];
    citationStyle: string;
}>();

const toast = useToast();
const isCiting = ref(false);

// Refs cited in the document, in order of appearance
const citedRefs = computed<ReferenceItem[]>(() => {
    if (!props.citedOrder.length) {
        return [];
    }

    const map = new Map(props.references.map((r) => [r.id, r]));

    return props.citedOrder
        .map((id) => map.get(id))
        .filter((r): r is ReferenceItem => r !== undefined);
});

// ── Style selector ────────────────────────────────────────────────────────────

const styleOptions: { value: CitationStyle; label: string }[] = [
    { value: 'apa', label: 'APA 7' },
    { value: 'chicago', label: 'Chicago' },
    { value: 'vancouver', label: 'Vancouver' },
];

const localStyle = ref<CitationStyle>(
    (props.citationStyle as CitationStyle) ?? 'apa',
);

async function onStyleChange(newStyle: string) {
    if (newStyle === localStyle.value) {
        return;
    }

    const style = newStyle as CitationStyle;
    localStyle.value = style;

    try {
        const res = await fetch(updateStyle.url(props.project.id), {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': getCsrf(),
            },
            body: JSON.stringify({ style }),
        });

        if (!res.ok) {
            throw new Error('style update failed');
        }

        router.reload({ only: ['citation_style'] });
    } catch {
        toast.error('Impossible de sauvegarder le style de citation.');
    }
}

// ── Cite action ───────────────────────────────────────────────────────────────

async function onCite(ref: ReferenceItem) {
    if (isCiting.value) {
        return;
    }

    isCiting.value = true;

    try {
        const res = await fetch(
            citePaper.url({ project: props.project.id, paper: ref.id }),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': getCsrf(),
                },
            },
        );

        if (!res.ok) {
            const data = await res.json().catch(() => null);
            toast.error(
                data?.message ?? "Erreur lors de l'ajout de la citation.",
            );

            return;
        }

        const data = await res.json();

        if (data.already_cited) {
            toast.info('Cette référence est déjà citée dans votre document.');
        } else {
            toast.success('Citation ajoutée à votre document.');
            router.reload({ only: ['cited_paper_ids'] });
        }
    } catch {
        toast.error("Erreur réseau lors de l'ajout de la citation.");
    } finally {
        isCiting.value = false;
    }
}

// ── Export actions ────────────────────────────────────────────────────────────

async function copyCitation() {
    const refsForStyle =
        localStyle.value === 'vancouver'
            ? citedRefs.value
            : sortForStyle(citedRefs.value, localStyle.value);

    try {
        await copyBibliography(refsForStyle, localStyle.value);
        toast.success(
            `Bibliographie ${localStyle.value.toUpperCase()} copiée dans le presse-papier.`,
        );
    } catch {
        toast.error('Impossible de copier dans le presse-papier.');
    }
}

function exportBibTeX() {
    downloadBibTeX(citedRefs.value, props.project.name);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getCsrf(): string {
    return decodeURIComponent(
        document.cookie
            .split('; ')
            .find((c) => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1] ?? '',
    );
}
</script>

<template>
    <div class="flex min-h-0 flex-1 overflow-hidden">
        <!-- Left: References panel -->
        <div
            class="flex min-h-0 min-w-0 flex-1 flex-col border-r border-border/40"
        >
            <ReferencesPanel
                :references="references"
                density="comfortable"
                :cited-ids="citedIds"
                :collections="zotero.collections"
                :store-url="referencesStore(project.id).url"
                :project-id="project.id"
                @cite="onCite"
            />
        </div>

        <!-- Right: Generated bibliography -->
        <div
            class="flex w-80 shrink-0 flex-col xl:w-96"
            data-tour="editor-bibliography"
        >
            <!-- Header -->
            <div
                class="shrink-0 border-b border-border/40 bg-background px-4 py-3"
            >
                <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-medium">Bibliographie</p>
                    <!-- Style selector -->
                    <select
                        data-tour="citation-style-select"
                        :value="localStyle"
                        class="h-7 rounded-md border border-border/60 bg-background px-2 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                        @change="
                            onStyleChange(
                                ($event.target as HTMLSelectElement).value,
                            )
                        "
                    >
                        <option
                            v-for="opt in styleOptions"
                            :key="opt.value"
                            :value="opt.value"
                        >
                            {{ opt.label }}
                        </option>
                    </select>
                </div>

                <!-- Action buttons -->
                <div class="mt-2 flex items-center gap-1.5">
                    <Button
                        size="sm"
                        variant="outline"
                        class="h-7 gap-1 text-xs"
                        :disabled="!citedRefs.length"
                        @click="copyCitation"
                    >
                        <ClipboardCopy class="h-3 w-3" />
                        Copier
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        class="h-7 gap-1 text-xs"
                        :disabled="!citedRefs.length"
                        @click="exportBibTeX"
                    >
                        <Download class="h-3 w-3" />
                        Exporter BibTeX
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        class="ml-auto h-7 gap-1 text-xs text-muted-foreground"
                        @click="router.reload()"
                    >
                        <RefreshCw class="h-3 w-3" />
                    </Button>
                </div>
            </div>

            <!-- Body -->
            <div class="flex-1 overflow-y-auto px-4 py-4">
                <BibliographyView
                    :references="references"
                    :cited-ids="citedIds"
                    :cited-order="citedOrder"
                    :style="localStyle"
                />
            </div>
        </div>
    </div>
</template>
